import { useDispatch } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { SETTINGS_HAS_UNSAVED_CHANGES_MODAL } from '../../../constants';
import { closeModal } from '../../../ducks';

export const KEEP_EDITS_BUTTON_TEST_ID = 'keep-settings-changes-button';
export const CLEAR_EDITS_BUTTON_TEST_ID = 'clear-settings-changes-button';
export const MODAL_TEXT_TEST_ID = 'modal-text';

export const KEEP_EDITING_BUTTON_TEXT = 'Keep editing';
export const SAVE_SETTINGS_BUTTON_TEXT = 'Save settings';

export const KEEP_EDITING_MODAL_TEXT =
  "This journey's settings have been edited, but there's a problem with one or more fields. Please fix the highlighted errors, then try again.";
export const SAVE_SETTINGS_MODAL_TEXT =
  "This journey's settings have been edited. Do you want to save your changes?";

export interface SettingsHasUnsavedChangesModalProps {
  /** Callback to discard changes */
  onDiscard?: () => void;
  /** callback to save changes */
  onSave: () => void;
  /** Whether the modal is open */
  isOpen: boolean;
  /** has validation errors */
  hasValidationErrors?: boolean;
}

export const SettingsHasUnsavedChangesModal: React.FC<
  React.PropsWithChildren<SettingsHasUnsavedChangesModalProps>
> = ({ onDiscard, onSave, isOpen, hasValidationErrors }) => {
  const dispatch = useDispatch();

  const handleCloseModal = () => dispatch(closeModal(SETTINGS_HAS_UNSAVED_CHANGES_MODAL));

  const handleDiscard = () => {
    onDiscard?.();
    handleCloseModal();
  };

  const handleUpdate = () => {
    onSave();
    handleCloseModal();
  };

  const buttonHandler = hasValidationErrors ? handleCloseModal : handleUpdate;

  const buttonText = hasValidationErrors ? KEEP_EDITING_BUTTON_TEXT : SAVE_SETTINGS_BUTTON_TEXT;

  const modalText = hasValidationErrors ? KEEP_EDITING_MODAL_TEXT : SAVE_SETTINGS_MODAL_TEXT;

  return (
    <Modal
      id={SETTINGS_HAS_UNSAVED_CHANGES_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>Unsaved changes</ModalHeader>
      <ModalBody>
        <p data-test={MODAL_TEXT_TEST_ID}>{modalText}</p>
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={CLEAR_EDITS_BUTTON_TEST_ID}
          onClick={handleDiscard}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          Discard changes
        </Button>
        <Button
          data-test={KEEP_EDITS_BUTTON_TEST_ID}
          autoFocus
          onClick={buttonHandler}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          {buttonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
